import {
  Filter
} from "lucide-react";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Card } from "primereact/card";
import { DataView } from "primereact/dataview";
import { useEffect, useState } from "react";
import ItemExtrato from "../model/extrato/ItemExtrato";
import { localDateStringToDate } from "../utils/convert";
import DocumentoExtrato from "../model/extrato/DocumentoExtrato";
import { Accordion, AccordionTab } from "primereact/accordion";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import ExtratoService from "../service/financeiro/ExtratoService";
import { useToastContext } from "../provider/ToastContext";
import EscolherRelatorioGeradoDialog from "./dialog/EscolherRelatorioGeradoDialog";
import EscolherAnoImpostoDialog from "./dialog/EscolherAnoImpostoDialog";

type Props = {
  itens: ItemExtrato[];
}

const service = new ExtratoService();
export function ExtratoComponent({ itens }: Props) {
  const [dataInicio, setDataInicio] = useState<Date>((new Date(new Date().getFullYear(), 0, 1)));
  const [dataFim, setDataFim] = useState<Date>((new Date(new Date().getFullYear(), 11, 31)));

  const [dialogEscolherRelatorio, setDialogEscolherRelatorio] = useState<{ visible: boolean, urls: string[], titulo: string }>({ visible: false, urls: [], titulo: "" });
  const [dialogImpostoRenda, setDialogImpostoRenda] = useState<{ visible: boolean }>({ visible: false });

  const { msgAlerta } = useToastContext();

  const [documentos, setDocumentos] = useState<DocumentoExtrato[]>([]);

  useEffect(() => {
    handleFiltrar();
  }, [itens]);

  const handleFiltrar = () => {

    if (dataInicio && dataFim) {
      const filtrados = itens.filter((item) => {
        const dataItem = new Date(item.dataPagamento);
        return dataItem.getTime() >= dataInicio.getTime() && dataItem.getTime() <= dataFim.getTime();
      });
      setDocumentos(itensToDocumentoExtrato(filtrados));
    } else if (dataInicio) {
      const filtrados = itens.filter((item) => {
        const dataItem = new Date(item.dataPagamento);
        return dataItem >= dataInicio;
      });
      setDocumentos(itensToDocumentoExtrato(filtrados));
    } else if (dataFim) {
      const filtrados = itens.filter((item) => {
        const dataItem = new Date(item.dataPagamento);
        return dataItem <= dataFim;
      });
      setDocumentos(itensToDocumentoExtrato(filtrados));
    } else {
      setDocumentos(itensToDocumentoExtrato(itens));
    }

  };

  const itensToDocumentoExtrato = (itens: ItemExtrato[]): DocumentoExtrato[] => {
    const documentos: DocumentoExtrato[] = [];
    itens.forEach((item) => {
      let documento = documentos.find((doc) => doc.documento === item.documento);
      if (!documento) {
        documento = new DocumentoExtrato(item.documento, []);
        documentos.push(documento);
      }
      documento.itens.push(item);
    });
    return documentos;
  }

  const parcelaTemplate = (rowData: ItemExtrato) => {
    return rowData.numeroParcela;
  };

  const vencimentoTemplate = (rowData: ItemExtrato) => {
    return localDateStringToDate(rowData.vencimento as unknown as string).toLocaleDateString("pt-BR");
  };

  const dataPagamentoTemplate = (rowData: ItemExtrato) => {
    return localDateStringToDate(rowData.dataPagamento as unknown as string).toLocaleDateString("pt-BR");
  };

  const valorOriginalTemplate = (rowData: ItemExtrato) => {
    return rowData.valorOriginal.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  };

  const valorPagamentoTemplate = (rowData: ItemExtrato) => {
    return rowData.valorPagamento.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  };

  const handleGerarSaldoDevedor = () => {
    service.buscarSaldoDevedor()
      .then((response) => {
        if (!response || response.results.length === 0) {
          msgAlerta("Saldo indisponível", "Saldo devedor não disponível no momento.");
          return;
        }
        setDialogEscolherRelatorio({
          visible: true,
          urls: response.results.map(r => r.urlReport),
          titulo: "Saldo Devedor"
        });
      })
      .catch((err) => {
        console.log(err);
      });
  }

  return (
    <div>
      {dialogEscolherRelatorio.visible ? <EscolherRelatorioGeradoDialog
        visible={dialogEscolherRelatorio.visible}
        onHide={() => setDialogEscolherRelatorio({ visible: false, urls: [], titulo: "" })}
        urls={dialogEscolherRelatorio.urls} titulo={dialogEscolherRelatorio.titulo} /> : null}
      
      {dialogImpostoRenda.visible ? <EscolherAnoImpostoDialog 
        visible={dialogImpostoRenda.visible}
        onHide={() => setDialogImpostoRenda({ visible: false })}
      />: null}
      <div className="mb-4">
        <h2>Extrato</h2>
        <p className="text-muted">Consulte o histórico de suas transações</p>
      </div>

      <Card
        title="Filtros"
        subTitle="Personalize sua busca por período"
        className="mb-4"
      >
        <div className="grid">
          <div className="col-12 md:col-3">
            <label htmlFor="data-inicio" className="block mb-2">
              Data Início
            </label>
            <Calendar
              id="data-inicio"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.value as Date)}
              dateFormat="dd/mm/yy"
              showIcon
              className="w-full"
            />
          </div>

          <div className="col-12 md:col-3">
            <label htmlFor="data-fim" className="block mb-2">
              Data Fim
            </label>
            <Calendar
              id="data-fim"
              value={dataFim}
              onChange={(e) => setDataFim(e.value as Date)}
              dateFormat="dd/mm/yy"
              showIcon
              className="w-full"
            />
          </div>

          <div className="col-12 md:col-3 flex align-items-end gap-2">
            <Button
              icon={<Filter size={18} />}
              label="Filtrar"
              className="flex-1"
              onClick={handleFiltrar}
            />
          </div>
        </div>
      </Card>

      <Card
        title={<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Transações</span>
          <div>
            <Button
              label="Buscar Saldo Devedor"
              style={{ marginRight: '10px' }}
              className="p-button-sm p-button-outlined"
              onClick={() => handleGerarSaldoDevedor()}
            />
            <Button
              label="Informe do Imposto de Renda"
              className="p-button-sm p-button-outlined"
              onClick={() => setDialogImpostoRenda({ visible: true })}
            />
          </div>

        </div>}
        subTitle={`${documentos.length} ${documentos.length === 1 ? "documento encontrado" : "documentos encontrados"
          }`}
      >
        <Accordion>
          {documentos.map((documento) => {
            return <AccordionTab key={documento.documento} header={documento.documento}>
              <DataTable
                header={"Extrato"}
                value={documento.itens}
                paginator rows={10}
                totalRecords={documento.itens.length}
                stripedRows
                emptyMessage="Nenhuma conta a pagar encontrada."
              >
                <Column header="Parcela" body={parcelaTemplate} />
                <Column header="Vencimento" body={vencimentoTemplate} />
                <Column header="Data pagamento" body={dataPagamentoTemplate} />
                <Column header="Valor original" body={valorOriginalTemplate} />
                <Column header="Valor do pagamento" body={valorPagamentoTemplate} />
              </DataTable>
            </AccordionTab>
          })}
        </Accordion>
      </Card>
    </div>
  );
}
